import { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import { cn } from "@/utils/cn";
import { useAuth } from "@/context/AuthContext";
import { canManageUsers, canViewUsers, canViewAudit, canManageDevices } from "@/config/roles";
import { devicesService } from "@/services";
import type { Role } from "@/services/types";

interface SettingsTabDef {
  key: string;
  label: string;
  icon: string;
  path: string;
  visible: (role: Role) => boolean;
}

const TABS: SettingsTabDef[] = [
  {
    key: "business",
    label: "Business profile",
    icon: "ri-store-2-line",
    path: "/settings/business",
    visible: (role) => role !== "viewer",
  },
  {
    key: "users",
    label: "Users",
    icon: "ri-user-settings-line",
    path: "/settings/users",
    visible: (role) => canViewUsers(role),
  },
  {
    key: "devices",
    label: "IP Approvals",
    icon: "ri-router-line",
    path: "/settings/devices",
    visible: (role) => canManageDevices(role),
  },
  {
    key: "permissions",
    label: "Permissions",
    icon: "ri-shield-check-line",
    path: "/settings/permissions",
    visible: () => true,
  },
  {
    key: "numbering",
    label: "Document numbering",
    icon: "ri-hashtag",
    path: "/settings/numbering",
    visible: (role) => canManageUsers(role),
  },
  // {
  //   key: "defaults",
  //   label: "Group defaults",
  //   icon: "ri-file-settings-line",
  //   path: "/settings/defaults",
  //   visible: (role) => role === "super_admin" || role === "admin",
  // },
  {
    key: "audit",
    label: "Audit log",
    icon: "ri-history-line",
    path: "/settings/audit",
    visible: (role) => canViewAudit(role),
  },
];

export function SettingsNav() {
  const { role } = useAuth();
  const [pendingDevicesCount, setPendingDevicesCount] = useState(0);

  useEffect(() => {
    let active = true;
    if (canManageDevices(role)) {
      devicesService
        .getPendingDeviceCount()
        .then((count) => {
          if (active) setPendingDevicesCount(count);
        })
        .catch(() => {});
    }
    return () => {
      active = false;
    };
  }, [role]);

  const visible = TABS.filter((t) => t.visible(role));
  return (
    <div className="mb-4 flex gap-1 overflow-x-auto border-b border-background-200">
      {visible.map((tab) => (
        <NavLink
          key={tab.key}
          to={tab.path}
          className={({ isActive }) =>
            cn(
              "relative flex cursor-pointer items-center gap-1.5 whitespace-nowrap px-3 py-2.5 text-sm font-medium transition-colors",
              isActive ? "text-primary-700" : "text-foreground-600 hover:text-foreground-900"
            )
          }
        >
          {({ isActive }) => (
            <>
              <i className={cn(tab.icon, "text-base", isActive ? "text-primary-600" : "text-foreground-400")} />
              {tab.label}
              {tab.key === "devices" && pendingDevicesCount > 0 ? (
                <span className="ml-1 inline-flex items-center justify-center rounded-full bg-warning-100 px-1.5 py-0.5 text-[10px] font-semibold text-warning-800">
                  {pendingDevicesCount}
                </span>
              ) : null}
              {isActive ? <span className="absolute inset-x-0 bottom-0 h-0.5 bg-primary-500" /> : null}
            </>
          )}
        </NavLink>
      ))}
    </div>
  );
}